import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Phone, 
  Flame,
  Droplets,
  Star,
  ChevronRight,
  ArrowRight,
  Calendar,
  Clock
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Toaster } from '../components/ui/sonner';
import { useContactSubmit, HoneypotField, CaptchaField, SmsConsentField, CityZipFields, locationSchemaFields } from '../components/contact/FormShared';
import { blogPosts } from '../components/BlogCard';
import { SeasonBadge, getCurrentSeason } from '../components/SeasonBadge';
import { TuneUpBanner, WinterServiceBanner } from '../components/TuneUpBanner';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '../components/ui/form';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const season = getCurrentSeason();
const seasonPosts = blogPosts.filter((p) => p.season === season);
const homePosts = (seasonPosts.length ? seasonPosts : blogPosts).slice(0, 4);
const GOOGLE_LOGO = "https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png";

const ASSETS = {
  hero: "https://customer-assets.emergentagent.com/job_5a91ec1d-c247-4280-a3f0-08c1958626f0/artifacts/31iugc6d_Screenshot%202026-02-24%20at%205.12.44%E2%80%AFPM.png",
  about: "https://images.pexels.com/photos/6653889/pexels-photo-6653889.jpeg?auto=compress&cs=tinysrgb&w=1200",
  difference: "https://customer-assets.emergentagent.com/job_5a91ec1d-c247-4280-a3f0-08c1958626f0/artifacts/h6btt28u_difference-pic.jpg",
  residential: "https://images.pexels.com/photos/6653889/pexels-photo-6653889.jpeg?auto=compress&cs=tinysrgb&w=800",
  commercial: "https://images.pexels.com/photos/29248902/pexels-photo-29248902.jpeg?auto=compress&cs=tinysrgb&w=800",
  boilers: "https://images.pexels.com/photos/7859953/pexels-photo-7859953.jpeg?auto=compress&cs=tinysrgb&w=800",
  waterHeaters: "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=800",
  remodeling: "https://images.pexels.com/photos/10827349/pexels-photo-10827349.jpeg?auto=compress&cs=tinysrgb&w=800",
  hvac: "https://static.prod-images.emergentagent.com/jobs/580a56e6-7aa2-42cb-a3cc-a9ea8b9dae1c/images/fc57f73308498e1d8160e5e6bb615ce07760500fbdee1579bd4258b4b388ee13.jpeg",
  testimonialBg: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1200",
  anniversary50Emblem: "https://customer-assets.emergentagent.com/job_5a91ec1d-c247-4280-a3f0-08c1958626f0/artifacts/2tfz4y04_BNPH_50th%20ANNV%20Emblem.png",
};

const services = [
  { title: 'RESIDENTIAL', image: ASSETS.residential, link: '/services/residential' },
  { title: 'COMMERCIAL', image: ASSETS.commercial, link: '/services/commercial' },
  { title: 'BOILERS', image: ASSETS.boilers, link: '/services/boilers' },
  { title: 'WATER HEATERS', image: ASSETS.waterHeaters, link: '/services/water-heaters' },
  { title: 'REMODELING', image: ASSETS.remodeling, link: '/services/remodeling' },
  { title: 'AIR CONDITIONING', image: ASSETS.hvac, link: '/services/air-conditioning' },
];

const bnphDifference = [
  'Superior Reputation',
  'Installation Process',
  'Older Home Experts',
  'Quality Workmanship',
  'No Surprise Pricing',
  'Fully Licensed & Insured',
];

const servicesList = [
  'Backflow Prevention',
  'Boilers – Steam / Hot Water',
  'Boiler Maintenance / Service',
  'Commercial Kitchens',
  'Faucet Install or Repair',
  'Garbage Disposals',
  'Gas Piping',
  'Industrial Piping',
  'Pipe Thawing',
  'Piping Replacement and Repair',
  'Plumbing Fixtures – Replace',
  'Pumps',
  'RPZ Testing and Rebuilding',
  'Reverse Osmosis Systems',
  'Sewage Ejectors',
  'Sump Pumps',
  'Toilets',
  'Washer / Dryer Hookups',
  'Water Filters',
  'Water Heaters',
  'Water Softeners',
  'Air Conditioning',
];

const serviceOptions = [
  'Residential Plumbing',
  'Commercial Plumbing',
  'Boilers',
  'Water Heaters',
  'Remodeling',
  'Air Conditioning',
  'Other',
];

const formSchema = z.object({
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().min(1, 'Required'),
  phone: z.string().min(10, 'Required'),
  email: z.string().email('Required'),
  service: z.string().min(1, 'Required'),
  ...locationSchemaFields(z),
  consent: z.boolean().optional(), // Made optional for TCR compliance
  website: z.string().max(0).optional(), // Honeypot field
});

const Home = () => {
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 4.8, total_reviews: 279 });
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { firstName: '', lastName: '', phone: '', email: '', city: '', zip: '', service: '', consent: false, website: '' },
  });

  const { onSubmit, isSubmitting, turnstileToken, setTurnstileToken } = useContactSubmit(form, {
    successMessage: 'Thank you! We will contact you shortly.',
    formName: 'homepage-quote',
    buildPayload: (data) => ({
      name: `${data.firstName} ${data.lastName}`,
      email: data.email,
      phone: data.phone,
      service: data.service,
      message: `Quote request for ${data.service}`,
    }),
  });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/reviews`);
        setReviews(response.data.reviews);
        setReviewStats({ average_rating: response.data.average_rating, total_reviews: response.data.total_reviews });
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };
    fetchReviews();
  }, []);

  return (
    <div data-testid="home-page" className="font-sans">
      <Toaster position="top-center" richColors />
      
      {/* Hero Section - Using Exact Reference Image */}
      <section className="relative" data-testid="hero-section">
        {/* Full Banner Image - Contains all text and design elements */}
        <div className="w-full relative">
          <img 
            src={ASSETS.hero} 
            alt="Your Trusted Plumbing and Heating Contractor Since 1976" 
            className="w-full h-auto object-cover"
          />
          {/* 50th Anniversary Emblem - Center Top - Hidden on mobile to avoid overlap */}
          <img 
            src={ASSETS.anniversary50Emblem}
            alt="50th Anniversary"
            className="absolute top-4 left-1/2 transform -translate-x-1/2 w-16 h-16 sm:w-20 sm:h-20 md:w-28 md:h-28 lg:w-36 lg:h-36 object-contain hidden sm:block"
          />
          {/* Clickable Learn More overlay */}
          <Link 
            to="/our-company"
            className="absolute bottom-[15%] left-1/2 transform -translate-x-1/2 bg-transparent w-40 h-14 cursor-pointer"
            aria-label="Learn More"
          />
        </div>
      </section>

      {/* Contact Form Section - Below Hero */}
      <section className="bg-[#F5F0EB] py-10" data-testid="contact-form-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-oswald uppercase text-2xl font-bold text-[#14366C] mb-4 text-center tracking-wide">
            Contact Us Today
          </h2>
          
          {/* Flame/Water Icon Divider */}
          <div className="flex items-center justify-center gap-0 mb-6">
            <div className="h-[2px] w-20 bg-[#14366C]" />
            <div className="relative mx-2">
              <Flame className="w-6 h-6 text-[#F58220]" />
              <Droplets className="w-4 h-4 text-[#00AEEF] absolute -bottom-1 left-1/2 -translate-x-1/2" />
            </div>
            <div className="h-[2px] w-20 bg-[#14366C]" />
          </div>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" data-testid="hero-contact-form">
              <HoneypotField form={form} />
              
              {/* Horizontal Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="First Name" {...field} className="bg-white border-gray-300 rounded h-12 px-4 text-sm" data-testid="home-firstname" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="Last Name" {...field} className="bg-white border-gray-300 rounded h-12 px-4 text-sm" data-testid="home-lastname" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input placeholder="Phone Number" {...field} className="bg-white border-gray-300 rounded h-12 px-4 text-sm" data-testid="home-phone" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input type="email" placeholder="Email Address" {...field} className="bg-white border-gray-300 rounded h-12 px-4 text-sm" data-testid="home-email" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <CityZipFields form={form} prefix="home" inputClassName="bg-white border-gray-300 rounded h-12 px-4 text-sm" />
                <FormField
                  control={form.control}
                  name="service"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-white border-gray-300 rounded h-12 px-4 text-sm" data-testid="home-service">
                            <SelectValue placeholder="Services Needed" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {serviceOptions.map((service) => (
                            <SelectItem key={service} value={service}>{service}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              
              <SmsConsentField form={form} testId="home-consent" textClassName="text-xs" />
              <CaptchaField setToken={setTurnstileToken} className="flex justify-center" />
              
              <div className="flex justify-center">
                <button 
                  type="submit" 
                  data-testid="home-contact-submit"
                  disabled={isSubmitting || !turnstileToken}
                  className="bg-[#F58220] hover:bg-[#e07010] text-white font-oswald font-bold uppercase tracking-wider px-12 py-3 text-sm transition-colors rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </Form>
        </div>
      </section>

      {/* Phone Number Bar */}
      <section className="bg-[#14366C] py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <a href="tel:651-738-9354" className="font-oswald text-2xl md:text-3xl font-bold text-white tracking-wider hover:text-[#F58220] transition-colors">
            651-738-9354
          </a>
        </div>
      </section>

      {/* Our Services Grid */}
      <section className="py-16 bg-[#F8F8F8]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Flame className="w-5 h-5 text-[#F58220]" />
              <Droplets className="w-5 h-5 text-[#00AEEF]" />
            </div>
            <h2 className="font-oswald uppercase text-3xl font-bold text-[#14366C] tracking-wide">Our Services</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <Link to={service.link} className="block group">
                  <div className="bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={service.image} alt={service.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="p-4 text-center">
                      <h3 className="font-oswald text-[#14366C] font-bold tracking-wide text-lg">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-oswald uppercase text-2xl md:text-3xl font-bold text-[#14366C] mb-6 leading-tight tracking-wide">
                Your Local Plumber in the Twin Cities - Serving St. Paul, Minneapolis & Surrounding Areas
              </h2>
              <p className="text-gray-600 mb-5 leading-relaxed">
                Looking for a reliable <strong>plumber near me</strong>? Bruce Nelson Plumbing & Heating Service (BNPH) is your trusted <strong>local plumbing company</strong> serving the Twin Cities since 1976. We are licensed, bonded and insured in the state of Minnesota, providing top-rated <strong>residential plumbing services</strong> and <strong>commercial plumbing services</strong> throughout <strong>St. Paul</strong>, <strong>Minneapolis</strong>, <strong>Oakdale</strong>, <strong>Woodbury</strong>, <strong>Eagan</strong>, <strong>Maplewood</strong>, and surrounding communities.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Whether you need <strong>water heater repair</strong>, <strong>boiler service</strong>, <strong>bathroom remodeling</strong>, or plumbing repairs, our expert technicians are here to help. From new construction to general maintenance, we're the <strong>best plumbing company</strong> in the Twin Cities area.
              </p>
              <Link to="/our-company" className="text-[#00AEEF] hover:text-[#008cc7] font-semibold inline-flex items-center gap-2 text-sm">
                Read More <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img src={ASSETS.difference} alt="Professional plumber at work" className="w-full shadow-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* The BNPH Difference */}
      <section className="py-12 bg-[#F8F8F8]" data-testid="difference-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-oswald uppercase text-2xl font-bold text-[#14366C] text-center mb-8 tracking-wide">
            The BNPH Difference
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
            {bnphDifference.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3"
              >
                <ChevronRight className="w-5 h-5 text-[#F58220] flex-shrink-0" />
                <span className="text-gray-700 font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews - ComfortMaster Style */}
      <section className="py-16 bg-white" data-testid="testimonial-section">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[#14366C] mb-4">
              See What Our Happy Customers Are Saying
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover why homeowners and businesses trust us as their go-to choice for reliable, high-quality plumbing and heating services in the Twin Cities.
            </p>
          </div>

          {/* Google Rating */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src={GOOGLE_LOGO} alt="Google" className="h-7" />
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-[#F5A623] fill-[#F5A623]" />
              ))}
            </div>
            <span className="font-semibold text-[#14366C]">{reviewStats.average_rating}</span>
            <span className="text-gray-500">Rating</span>
          </div>

          {/* Map */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="aspect-[16/9] md:aspect-[21/9] w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2822.7!2d-92.963!3d44.963!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87f7d6a2678eea6b%3A0x53893f88d32197f2!2sBruce%20Nelson%20Plumbing%20%26%20Heating%20Services%2C%20LLC.!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="BNPH Location Map"
              />
            </div>
          </div>

          {/* Read Reviews Button */}
          <div className="text-center mb-12">
            <a
              href="https://www.google.com/search?q=Bruce+Nelson+Plumbing+%26+Heating+Services+reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#137333] hover:bg-[#0f5a28] text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              <span className="text-white font-bold">G</span>
              Read Our Google Reviews
            </a>
          </div>

          {/* Review Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((review, index) => (
              <motion.div
                key={review.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#F5A623] fill-[#F5A623]" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  "{review.text}"
                </p>
                <p className="font-semibold text-[#14366C]">{review.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services List */}
      <section className="py-16 bg-white" data-testid="services-list-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Flame className="w-5 h-5 text-[#F58220]" />
              <Droplets className="w-5 h-5 text-[#00AEEF]" />
            </div>
            <h2 className="font-oswald uppercase text-3xl font-bold text-[#14366C] tracking-wide">Our Services</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-3">
            {servicesList.map((service, index) => (
              <motion.div
                key={service}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.02 }}
                className="flex items-center gap-2"
              >
                <ChevronRight className="w-4 h-4 text-[#F58220] flex-shrink-0" />
                <span className="text-gray-700 text-sm">{service}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Seasonal Homepage Banner: fall tune-up (Sep–Nov), winter service (Dec–Feb) */}
      {season === 'fall' && (
        <section className="pt-16 bg-white" data-testid="home-tune-up-section">
          <div className="max-w-5xl mx-auto px-4">
            <TuneUpBanner className="" />
          </div>
        </section>
      )}
      {season === 'winter' && (
        <section className="pt-16 bg-white" data-testid="home-winter-service-section">
          <div className="max-w-5xl mx-auto px-4">
            <WinterServiceBanner className="" />
          </div>
        </section>
      )}

      {/* Seasonal Blog Section */}
      <section className="py-16 bg-white" data-testid="blog-section">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            {season && <SeasonBadge season={season} className="px-4 py-2 text-sm mb-4" />}
            <h2 className="font-oswald text-3xl md:text-4xl font-bold text-[#14366C] mb-4">
              Expert Tips for Minnesota Homeowners
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {season === 'winter' ? 'Stay warm and protect your plumbing all winter with our expert guides.' : season === 'fall' ? 'Prepare your home for winter with our expert guides on heating, plumbing, and home maintenance.' : 'Expert guides on heating, plumbing, and home maintenance for Minnesota homeowners.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {homePosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={`/blog/${post.id}`}
                  className="group block bg-[#F5F0EB] rounded-lg overflow-hidden hover:shadow-lg transition-all"
                  data-testid={`home-blog-card-${post.id}`}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <SeasonBadge season={post.season} short className="px-2 py-0.5 text-xs" />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="font-oswald font-bold text-[#14366C] group-hover:text-[#F58220] transition-colors text-lg line-clamp-2 mb-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {post.excerpt}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link 
              to="/blog"
              data-testid="home-view-all-articles"
              className="inline-flex items-center bg-[#14366C] hover:bg-[#0d2a52] text-white font-oswald font-bold uppercase tracking-wider px-8 py-3 rounded-md transition-colors"
            >
              View All Articles
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Service Areas Section */}
      <section className="py-12 bg-[#F8F8F8]" data-testid="service-areas-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-oswald uppercase text-2xl font-bold text-[#14366C] text-center mb-6">
            Proudly Serving the Twin Cities Metro Area
          </h2>
          <p className="text-gray-600 text-center mb-8 max-w-3xl mx-auto">
            As the <strong>best plumber</strong> in the Twin Cities, we provide professional plumbing and heating services to homeowners and businesses throughout the metro area. Find a <strong>plumber near me</strong> has never been easier.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 text-center">
            {[
              { name: 'Saint Paul', slug: 'saint-paul' },
              { name: 'Minneapolis', slug: 'minneapolis' },
              { name: 'Oakdale', slug: 'oakdale' },
              { name: 'Woodbury', slug: 'woodbury' },
              { name: 'Eagan', slug: 'eagan' },
              { name: 'Maplewood', slug: 'maplewood' },
              { name: 'Cottage Grove', slug: 'cottage-grove' },
              { name: 'Lake Elmo', slug: 'lake-elmo' },
              { name: 'Newport', slug: 'newport' },
              { name: 'Rosemount', slug: 'rosemount' }
            ].map((city) => (
              <Link 
                key={city.slug} 
                to={`/plumber-${city.slug}`}
                className="text-gray-700 hover:text-[#F58220] transition-colors"
              >
                <span className="font-medium">Plumber {city.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 bg-[#14366C]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <h2 className="font-oswald uppercase text-2xl font-bold text-white tracking-wide">
              Ready to Get Started?
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <button className="bg-[#F58220] hover:bg-[#e07010] text-white font-oswald font-bold uppercase tracking-wider px-6 py-3 text-sm transition-colors">
                  Contact Us
                </button>
              </Link>
              <a href="tel:651-738-9354">
                <button className="bg-white text-[#14366C] font-oswald font-bold uppercase tracking-wider px-6 py-3 text-sm hover:bg-gray-100 transition-colors flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  651-738-9354
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
